import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Shield, ShieldCheck, Send, UserPlus, LogIn, Check } from 'lucide-react';
import ComplianceFooter from './ComplianceFooter';
import { User } from '../types';

interface LoginProps {
  onLoginSuccess: (user: User) => void;
  isAuthenticated: boolean;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess, isAuthenticated }) => {
  const navigate = useNavigate();
  const [isRegisterMode, setIsRegisterMode] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    company: '',
    companyAddress: '',
    streetNumber: '',
    zipCode: '',
    city: '',
    country: '',
    title: '',
    gender: '',
    emailVerificationCode: '',
    phone: '',
    smsCode: '',
    ifedAccessCode: '',
    userPassword: '',
    countryCode: '+41'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSendingEmailCode, setIsSendingEmailCode] = useState(false);
  const [isSendingSmsCode, setIsSendingSmsCode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // API base URL - use environment variable or fallback to deployed backend
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://data-room-api.onrender.com';
  
  // Add retry logic for failed requests
  const fetchWithRetry = async (url: string, options: RequestInit, maxRetries = 3): Promise<Response> => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const response = await fetch(url, options);
        return response;
      } catch (error) {
        console.log(`🔧 DEBUG: Attempt ${i + 1} failed:`, error);
        if (i === maxRetries - 1) throw error;
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
    throw new Error('All retry attempts failed');
  };
  console.log('🔧 DEBUG: API_BASE_URL:', API_BASE_URL);
  console.log('🔧 DEBUG: process.env.REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
  console.log('🔧 DEBUG: Full API URL:', `${API_BASE_URL}/api/auth/send-verification`);
  console.log('🔧 DEBUG: Deployment timestamp:', new Date().toISOString());

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const resetForm = () => {
          setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        company: '',
        companyAddress: '',
        streetNumber: '',
        zipCode: '',
        city: '',
        country: '',
        title: '',
        gender: '',
        emailVerificationCode: '',
        phone: '',
        smsCode: '',
        ifedAccessCode: '',
        userPassword: '',
        countryCode: '+41'
      });
    setEmailSent(false);
    setEmailVerified(false);
    setSmsSent(false);
    setError('');
    setSuccess('');
    setLoading(false);
    setIsSendingEmailCode(false);
    setIsSendingSmsCode(false);
  };

  const handleSendCode = async () => {
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    setIsSendingEmailCode(true);
    setError('');

    try {
      console.log('🔧 DEBUG: Sending verification code to:', formData.email);
      console.log('🔧 DEBUG: Using API URL:', `${API_BASE_URL}/api/auth/send-verification`);
      
      const requestBody = JSON.stringify({ email: formData.email });
      console.log('🔧 DEBUG: Request body:', requestBody);
      
      const response = await fetchWithRetry(`${API_BASE_URL}/api/auth/send-verification`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: requestBody
      });

      console.log('🔧 DEBUG: Response status:', response.status);
      console.log('🔧 DEBUG: Response ok:', response.ok);
      console.log('🔧 DEBUG: Response headers:', Object.fromEntries(response.headers.entries()));

      if (response.ok) {
        const data = await response.json();
        console.log('🔧 DEBUG: Success response data:', data);
        setEmailSent(true);
        setSuccess('Verification code sent to your email');
      } else {
        const errorText = await response.text();
        console.log('🔧 DEBUG: Error response text:', errorText);
        try {
          const data = JSON.parse(errorText);
          console.log('🔧 DEBUG: Error response data:', data);
          setError(data.error || 'Failed to send verification code');
        } catch (parseError) {
          console.log('🔧 DEBUG: Could not parse error response as JSON');
          setError(`Failed to send verification code. Status: ${response.status}`);
        }
      }
    } catch (err) {
      console.error('🔧 DEBUG: Fetch error:', err);
      setError('Failed to send verification code. Please try again.');
    } finally {
      setIsSendingEmailCode(false);
    }
  };

  const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Format Swiss numbers (assuming Swiss numbers start with 07)
    if (cleaned.startsWith('07') && cleaned.length >= 9) {
      return `+41${cleaned.substring(1)}`;
    }
    
    // For other numbers, just add the country code
    return `${formData.countryCode}${cleaned}`;
  };

  const handleSendSmsCode = async () => {
    if (!formData.phone) {
      setError('Please enter your phone number');
      return;
    }

    setIsSendingSmsCode(true);
    setError('');

    try {
      const formattedPhone = formatPhoneNumber(formData.phone);
      console.log('🔧 DEBUG: Sending SMS to formatted phone:', formattedPhone);
      console.log('🔧 DEBUG: Using API URL:', `${API_BASE_URL}/api/auth/send-sms-code`);
      
      const response = await fetchWithRetry(`${API_BASE_URL}/api/auth/send-sms-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formattedPhone })
      });

      console.log('🔧 DEBUG: SMS Response status:', response.status);
      console.log('🔧 DEBUG: SMS Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('🔧 DEBUG: SMS Success response:', data);
        setSmsSent(true);
        setSuccess('SMS verification code sent to your phone');
      } else {
        const errorText = await response.text();
        console.log('🔧 DEBUG: SMS Error response text:', errorText);
        try {
          const data = JSON.parse(errorText);
          console.log('🔧 DEBUG: SMS Error response data:', data);
          setError(data.error || 'Failed to send SMS code');
        } catch (parseError) {
          console.log('🔧 DEBUG: Could not parse SMS error response as JSON');
          setError(`Failed to send SMS code. Status: ${response.status}`);
        }
      }
    } catch (err) {
      console.error('🔧 DEBUG: SMS Fetch error:', err);
      setError('Failed to send SMS code. Please try again.');
    } finally {
      setIsSendingSmsCode(false);
    }
  };

  const handleEmailVerification = async () => {
    if (!formData.emailVerificationCode) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: formData.email, 
          code: formData.emailVerificationCode 
        })
      });

      if (response.ok) {
        setEmailVerified(true);
        setSuccess('Email verified successfully! ✓');
      } else {
        const data = await response.json();
        setError(data.error || 'Invalid verification code');
      }
    } catch (err) {
      setError('Failed to verify email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isRegisterMode) {
        // Registration
        const formattedPhone = formatPhoneNumber(formData.phone);
        const registrationData = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          company: formData.company,
          companyAddress: formData.companyAddress,
          streetNumber: formData.streetNumber,
          zipCode: formData.zipCode,
          city: formData.city,
          country: formData.country,
          title: formData.title,
          gender: formData.gender,
          phone: formattedPhone,
          smsCode: formData.smsCode,
          emailVerificationCode: formData.emailVerificationCode,
          ifedAccessCode: formData.ifedAccessCode
        };
        
        console.log('🔧 DEBUG: Registration data:', registrationData);
        console.log('🔧 DEBUG: Registration API URL:', `${API_BASE_URL}/api/auth/register`);
        
        const response = await fetchWithRetry(`${API_BASE_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(registrationData)
        });

        console.log('🔧 DEBUG: Registration Response status:', response.status);
        console.log('🔧 DEBUG: Registration Response ok:', response.ok);

        if (response.ok) {
          const data = await response.json();
          console.log('🔧 DEBUG: Registration Success response:', data);
          // Store the JWT token in localStorage
          if (data.token) {
            localStorage.setItem('token', data.token);
            console.log('🔧 DEBUG: JWT token stored in localStorage');
          }
          onLoginSuccess(data.user);
          navigate('/nda');
        } else {
          const errorText = await response.text();
          console.log('🔧 DEBUG: Registration Error response text:', errorText);
          try {
            const data = JSON.parse(errorText);
            console.log('🔧 DEBUG: Registration Error response data:', data);
            setError(data.error || 'Registration failed');
          } catch (parseError) {
            console.log('🔧 DEBUG: Could not parse registration error response as JSON');
            setError(`Registration failed. Status: ${response.status}`);
          }
        }
      } else {
        // Login
        const response = await fetchWithRetry(`${API_BASE_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            emailVerificationCode: formData.emailVerificationCode
          })
        });

        if (response.ok) {
          const data = await response.json();
          // Store the JWT token in localStorage
          if (data.token) {
            localStorage.setItem('token', data.token);
            console.log('🔧 DEBUG: JWT token stored in localStorage');
          }
          onLoginSuccess(data.user);
          navigate('/nda');
        } else {
          const data = await response.json();
          setError(data.error || 'Login failed');
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="min-h-screen flex items-center justify-center bg-white p-4"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
                      <div className="text-center mb-4">
              <Shield className="w-8 h-8 text-primary-600 mx-auto mb-2" />
              <h1 className="text-2xl font-bold text-neutral-900">IFED<br />Secure Data Room</h1>
            </div>
          <p className="text-neutral-600">
            {isRegisterMode ? 'Create your secure account' : 'Welcome back'}
          </p>
        </div>

        <div className="flex items-center justify-center mb-6">
          <button
            onClick={() => {
              setIsRegisterMode(true);
              resetForm();
            }}
            className={`px-4 py-2 rounded-l-lg text-sm font-medium transition-colors ${
              isRegisterMode
                ? 'bg-primary-600 text-white'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            <UserPlus className="w-4 h-4 inline mr-2" />
            Register
          </button>
          <button
            onClick={() => {
              setIsRegisterMode(false);
              resetForm();
            }}
            className={`px-4 py-2 rounded-r-lg text-sm font-medium transition-colors ${
              !isRegisterMode
                ? 'bg-primary-600 text-white'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            <LogIn className="w-4 h-4 inline mr-2" />
            Login
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
              Email Address
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="input-field flex-1"
                placeholder="Enter your email address"
                required
              />
              {!emailSent && (
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={isSendingEmailCode || !formData.email}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isSendingEmailCode ? 'Sending...' : 'Send Code'}
                </button>
              )}
            </div>
          </div>

          {/* Email Verification Code Field */}
          {emailSent && (
            <div>
              <label htmlFor="emailVerificationCode" className="block text-sm font-medium text-neutral-700 mb-1">
                Email Verification Code
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="emailVerificationCode"
                  name="emailVerificationCode"
                  value={formData.emailVerificationCode}
                  onChange={handleInputChange}
                  className="input-field pr-12"
                  placeholder="Enter 6-digit code from email"
                  maxLength={6}
                  required
                />
                              {emailVerified ? (
                <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
              ) : (
                <Send className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
              )}
              </div>
              <p className="text-xs text-neutral-500 mt-1">
                Enter the 6-digit verification code sent to your email
              </p>
              {!emailVerified && isRegisterMode && (
                <button
                  type="button"
                  onClick={handleEmailVerification}
                  className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Verify Email
                </button>
              )}
            </div>
          )}

          {/* Registration Fields - Show immediately in register mode */}
          {isRegisterMode && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-3"
            >
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-neutral-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Enter your first name"
                  required
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-neutral-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Enter your last name"
                  required
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-neutral-700 mb-1">
                  Company *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Enter your company name"
                  required
                />
              </div>

              <div>
                <label htmlFor="companyAddress" className="block text-sm font-medium text-neutral-700 mb-1">
                  Company Address
                </label>
                <input
                  type="text"
                  id="companyAddress"
                  name="companyAddress"
                  value={formData.companyAddress}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Enter your company address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="streetNumber" className="block text-sm font-medium text-neutral-700 mb-1">
                    Street Number
                  </label>
                  <input
                    type="text"
                    id="streetNumber"
                    name="streetNumber"
                    value={formData.streetNumber}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="Enter street number"
                  />
                </div>

                <div>
                  <label htmlFor="zipCode" className="block text-sm font-medium text-neutral-700 mb-1">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="Enter ZIP code"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-neutral-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="Enter city"
                  />
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-neutral-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="Enter country"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="title" className="block text-sm font-medium text-neutral-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Enter your title"
                />
              </div>

              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-neutral-700 mb-1">
                  Gender
                </label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="px-2 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-sm w-full"
                >
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="ifedAccessCode" className="block text-sm font-medium text-neutral-700 mb-1">
                  IFED Access Code *
                </label>
                <input
                  type="text"
                  id="ifedAccessCode"
                  name="ifedAccessCode"
                  value={formData.ifedAccessCode}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Enter IFED access code"
                  required
                />
              </div>
            </motion.div>
          )}

          {/* Phone Number Field - Only for Registration */}
          {isRegisterMode && (
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-neutral-700 mb-1">
                Phone Number *
              </label>
              <div className="flex gap-2">
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleInputChange}
                  className="px-2 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-sm w-24"
                >
                  <option value="+41">+41</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+49">+49</option>
                  <option value="+33">+33</option>
                  <option value="+39">+39</option>
                  <option value="+34">+34</option>
                  <option value="+31">+31</option>
                  <option value="+32">+32</option>
                  <option value="+46">+46</option>
                  <option value="+45">+45</option>
                  <option value="+47">+47</option>
                  <option value="+358">+358</option>
                  <option value="+43">+43</option>
                  <option value="+48">+48</option>
                  <option value="+420">+420</option>
                  <option value="+36">+36</option>
                  <option value="+30">+30</option>
                  <option value="+351">+351</option>
                  <option value="+353">+353</option>
                  <option value="+372">+372</option>
                  <option value="+371">+371</option>
                  <option value="+370">+370</option>
                  <option value="+373">+373</option>
                  <option value="+374">+374</option>
                  <option value="+375">+375</option>
                  <option value="+376">+376</option>
                  <option value="+377">+377</option>
                  <option value="+378">+378</option>
                  <option value="+379">+379</option>
                  <option value="+380">+380</option>
                  <option value="+381">+381</option>
                  <option value="+382">+382</option>
                  <option value="+383">+383</option>
                  <option value="+384">+384</option>
                  <option value="+385">+385</option>
                  <option value="+386">+386</option>
                  <option value="+387">+387</option>
                  <option value="+388">+388</option>
                  <option value="+389">+389</option>
                  <option value="+390">+390</option>
                  <option value="+391">+391</option>
                  <option value="+392">+392</option>
                  <option value="+393">+393</option>
                  <option value="+394">+394</option>
                  <option value="+395">+395</option>
                  <option value="+396">+396</option>
                  <option value="+397">+397</option>
                  <option value="+398">+398</option>
                  <option value="+399">+399</option>
                  <option value="+400">+400</option>
                  <option value="+401">+401</option>
                  <option value="+402">+402</option>
                  <option value="+403">+403</option>
                  <option value="+404">+404</option>
                  <option value="+405">+405</option>
                  <option value="+406">+406</option>
                  <option value="+407">+407</option>
                  <option value="+408">+408</option>
                  <option value="+409">+409</option>
                  <option value="+410">+410</option>
                  <option value="+411">+411</option>
                  <option value="+412">+412</option>
                  <option value="+413">+413</option>
                  <option value="+414">+414</option>
                  <option value="+415">+415</option>
                  <option value="+416">+416</option>
                  <option value="+417">+417</option>
                  <option value="+418">+418</option>
                  <option value="+419">+419</option>
                  <option value="+420">+420</option>
                  <option value="+421">+421</option>
                  <option value="+422">+422</option>
                  <option value="+423">+423</option>
                  <option value="+424">+424</option>
                  <option value="+425">+425</option>
                  <option value="+426">+426</option>
                  <option value="+427">+427</option>
                  <option value="+428">+428</option>
                  <option value="+429">+429</option>
                  <option value="+430">+430</option>
                  <option value="+431">+431</option>
                  <option value="+432">+432</option>
                  <option value="+433">+433</option>
                  <option value="+434">+434</option>
                  <option value="+435">+435</option>
                  <option value="+436">+436</option>
                  <option value="+437">+437</option>
                  <option value="+438">+438</option>
                  <option value="+439">+439</option>
                  <option value="+440">+440</option>
                  <option value="+441">+441</option>
                  <option value="+442">+442</option>
                  <option value="+443">+443</option>
                  <option value="+444">+444</option>
                  <option value="+445">+445</option>
                  <option value="+446">+446</option>
                  <option value="+447">+447</option>
                  <option value="+448">+448</option>
                  <option value="+449">+449</option>
                  <option value="+450">+450</option>
                  <option value="+451">+451</option>
                  <option value="+452">+452</option>
                  <option value="+453">+453</option>
                  <option value="+454">+454</option>
                  <option value="+455">+455</option>
                  <option value="+456">+456</option>
                  <option value="+457">+457</option>
                  <option value="+458">+458</option>
                  <option value="+459">+459</option>
                  <option value="+460">+460</option>
                  <option value="+461">+461</option>
                  <option value="+462">+462</option>
                  <option value="+463">+463</option>
                  <option value="+464">+464</option>
                  <option value="+465">+465</option>
                  <option value="+466">+466</option>
                  <option value="+467">+467</option>
                  <option value="+468">+468</option>
                  <option value="+469">+469</option>
                  <option value="+470">+470</option>
                  <option value="+471">+471</option>
                  <option value="+472">+472</option>
                  <option value="+473">+473</option>
                  <option value="+474">+474</option>
                  <option value="+475">+475</option>
                  <option value="+476">+476</option>
                  <option value="+477">+477</option>
                  <option value="+478">+478</option>
                  <option value="+479">+479</option>
                  <option value="+480">+480</option>
                  <option value="+481">+481</option>
                  <option value="+482">+482</option>
                  <option value="+483">+483</option>
                  <option value="+484">+484</option>
                  <option value="+485">+485</option>
                  <option value="+486">+486</option>
                  <option value="+487">+487</option>
                  <option value="+488">+488</option>
                  <option value="+489">+489</option>
                  <option value="+490">+490</option>
                  <option value="+491">+491</option>
                  <option value="+492">+492</option>
                  <option value="+493">+493</option>
                  <option value="+494">+494</option>
                  <option value="+495">+495</option>
                  <option value="+496">+496</option>
                  <option value="+497">+497</option>
                  <option value="+498">+498</option>
                  <option value="+499">+499</option>
                  <option value="+500">+500</option>
                  <option value="+501">+501</option>
                  <option value="+502">+502</option>
                  <option value="+503">+503</option>
                  <option value="+504">+504</option>
                  <option value="+505">+505</option>
                  <option value="+506">+506</option>
                  <option value="+507">+507</option>
                  <option value="+508">+508</option>
                  <option value="+509">+509</option>
                  <option value="+590">+590</option>
                  <option value="+591">+591</option>
                  <option value="+592">+592</option>
                  <option value="+593">+593</option>
                  <option value="+594">+594</option>
                  <option value="+595">+595</option>
                  <option value="+596">+596</option>
                  <option value="+597">+597</option>
                  <option value="+598">+598</option>
                  <option value="+599">+599</option>
                  <option value="+670">+670</option>
                  <option value="+672">+672</option>
                  <option value="+673">+673</option>
                  <option value="+674">+674</option>
                  <option value="+675">+675</option>
                  <option value="+676">+676</option>
                  <option value="+677">+677</option>
                  <option value="+678">+678</option>
                  <option value="+679">+679</option>
                  <option value="+680">+680</option>
                  <option value="+681">+681</option>
                  <option value="+682">+682</option>
                  <option value="+683">+683</option>
                  <option value="+685">+685</option>
                  <option value="+686">+686</option>
                  <option value="+687">+687</option>
                  <option value="+688">+688</option>
                  <option value="+689">+689</option>
                  <option value="+690">+690</option>
                  <option value="+691">+691</option>
                  <option value="+692">+692</option>
                  <option value="+850">+850</option>
                  <option value="+852">+852</option>
                  <option value="+853">+853</option>
                  <option value="+855">+855</option>
                  <option value="+856">+856</option>
                  <option value="+880">+880</option>
                  <option value="+886">+886</option>
                  <option value="+960">+960</option>
                  <option value="+961">+961</option>
                  <option value="+962">+962</option>
                  <option value="+963">+963</option>
                  <option value="+964">+964</option>
                  <option value="+965">+965</option>
                  <option value="+966">+966</option>
                  <option value="+967">+967</option>
                  <option value="+968">+968</option>
                  <option value="+970">+970</option>
                  <option value="+971">+971</option>
                  <option value="+972">+972</option>
                  <option value="+973">+973</option>
                  <option value="+974">+974</option>
                  <option value="+975">+975</option>
                  <option value="+976">+976</option>
                  <option value="+977">+977</option>
                  <option value="+98">+98</option>
                  <option value="+992">+992</option>
                  <option value="+993">+993</option>
                  <option value="+994">+994</option>
                  <option value="+995">+995</option>
                  <option value="+996">+996</option>
                  <option value="+998">+998</option>
                  <option value="+1242">+1242</option>
                  <option value="+1246">+1246</option>
                  <option value="+1264">+1264</option>
                  <option value="+1268">+1268</option>
                  <option value="+1284">+1284</option>
                  <option value="+1340">+1340</option>
                  <option value="+1345">+1345</option>
                  <option value="+1441">+1441</option>
                  <option value="+1473">+1473</option>
                  <option value="+1649">+1649</option>
                  <option value="+1664">+1664</option>
                  <option value="+1684">+1684</option>
                  <option value="+1758">+1758</option>
                  <option value="+1767">+1767</option>
                  <option value="+1784">+1784</option>
                  <option value="+1787">+1787</option>
                  <option value="+1809">+1809</option>
                  <option value="+1868">+1868</option>
                  <option value="+1869">+1869</option>
                  <option value="+1876">+1876</option>
                  <option value="+1939">+1939</option>
                </select>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="input-field flex-1"
                  placeholder="Enter your phone number"
                  required
                />
              </div>
              <button
                type="button"
                onClick={handleSendSmsCode}
                disabled={isSendingSmsCode || !formData.phone || smsSent}
                className="mt-2 px-4 py-2 bg-[#cba864] text-white rounded-lg hover:bg-[#b89654] disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
              >
                {isSendingSmsCode ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send SMS Code
                  </>
                )}
              </button>
            </div>
          )}

          {/* SMS Verification Code Field - Only for Registration */}
          {isRegisterMode && smsSent && (
            <div>
              <label htmlFor="smsCode" className="block text-sm font-medium text-neutral-700 mb-2">
                SMS Verification Code *
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="smsCode"
                  name="smsCode"
                  value={formData.smsCode}
                  onChange={handleInputChange}
                  className="input-field pr-12"
                  placeholder="Enter 6-digit SMS code"
                  maxLength={6}
                  required
                />
                <Send className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
              </div>
              <p className="text-xs text-neutral-500 mt-1">
                Enter the 6-digit verification code sent to your phone
              </p>
            </div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
            >
              {error}
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm"
            >
              {success}
            </motion.div>
          )}

          {/* Submit Button Logic */}
          {((isRegisterMode && formData.email && formData.firstName && formData.lastName && formData.company && formData.phone && formData.smsCode && formData.emailVerificationCode && formData.ifedAccessCode) || 
            (!isRegisterMode && formData.email && formData.emailVerificationCode)) && (
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isRegisterMode ? 'Creating Account...' : 'Authenticating...'}
                </div>
              ) : (
                isRegisterMode ? 'Create Account & Continue' : 'Authenticate & Continue'
              )}
            </button>
          )}
        </form>

        <ComplianceFooter />
      </div>
    </motion.div>
  );
};

export default Login; 